import axios from "axios";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
export const API_URL = `${BASE}/api`;

export const api = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

// ---------- helpers de storage (bearer + refresh) ----------
export const tokenStore = {
  getAccess: () => localStorage.getItem("td_token"),
  getRefresh: () => localStorage.getItem("td_refresh"),
  setPair: (access, refresh) => {
    if (access) localStorage.setItem("td_token", access);
    if (refresh) localStorage.setItem("td_refresh", refresh);
  },
  clear: () => {
    localStorage.removeItem("td_token");
    localStorage.removeItem("td_refresh");
    localStorage.removeItem("td_user");
  },
};

api.interceptors.request.use((config) => {
  const token = tokenStore.getAccess();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------- refresh silencioso ----------
let refreshPromise = null;

async function tryRefresh() {
  if (refreshPromise) return refreshPromise;
  const refresh = tokenStore.getRefresh();
  if (!refresh) return null;
  refreshPromise = axios
    .post(`${API_URL}/auth/refresh`, { refresh_token: refresh })
    .then((r) => {
      tokenStore.setPair(r.data.access_token, r.data.refresh_token);
      return r.data.access_token;
    })
    .catch(() => null)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

function isAuthEndpoint(url) {
  return typeof url === "string" && /\/auth\/(login|refresh|logout)/.test(url);
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const { response, config } = err || {};
    if (!response || !config) return Promise.reject(err);

    // 429 → rate limit
    if (response.status === 429) {
      const retry = response.headers?.["retry-after"];
      toast.error(
        `Demasiadas peticiones. Reintenta ${retry ? `en ${retry}s` : "en un momento"}.`,
        { id: "rate-limit" }
      );
      return Promise.reject(err);
    }

    // 423 → cuenta bloqueada (brute-force)
    if (response.status === 423) {
      const detail = response.data?.detail;
      if (typeof detail === "string") toast.error(detail, { id: "account-locked" });
      return Promise.reject(err);
    }

    // 401 → intento de refresh silencioso (una sola vez, no en endpoints de auth)
    if (response.status === 401 && !config._retried && !isAuthEndpoint(config.url)) {
      config._retried = true;
      const newAccess = await tryRefresh();
      if (newAccess) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newAccess}`;
        return api.request(config);
      }
      // refresh falló → logout local
      tokenStore.clear();
      toast.error("Tu sesión expiró. Ingresa de nuevo.", { id: "session-expired" });
      if (window.location.pathname !== "/login") window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);
